import axios from 'axios';
import VideoThumbnail from '../../../public/Images/video.png';
import { useEffect, useState } from 'react';
import { PlayCircleIcon } from '@phosphor-icons/react';

const buildVdoCipherIframeSrc = (otp, playbackInfo) =>
  `https://player.vdocipher.com/v2/?otp=${encodeURIComponent(otp)}&playbackInfo=${encodeURIComponent(playbackInfo)}`;

const VideoCipherPlayer = ({
  videoId,
  isSolution = true,
  questionId = null,
  videoThumbnail = null,
  testId,
  testTitle = null,
  /** Fetch OTP on mount and show the player (e.g. inside a modal). */
  autoLoad = false,
  /** When true, do not emit webengage events from this component (parent handles analytics). */
  skipInternalTracking = false,
}) => {
  const [otp, setOtp] = useState('');
  const [playbackInfo, setPlaybackInfo] = useState('');
  const [showPlayer, setShowPlayer] = useState(false);
  const [autoLoadError, setAutoLoadError] = useState(false);
  const [autoLoadPending, setAutoLoadPending] = useState(autoLoad);

  const user = JSON.parse(localStorage.getItem('userProfile'));

  const getVideoCipherOtp = async () => {
    if (!videoId) return;
    try {
      const res = await axios.get(`/api/video/${videoId}/getotp`);
      return {
        otp: res?.data?.data?.otp,
        playBackInfo: res?.data?.data?.playbackInfo,
      };
    } catch (err) {
      console.error(`Error fetching OTP and playback info for videoId ${videoId}`, err);
    }
  };

  const trackPlayback = () => {
    if (skipInternalTracking) return;
    window.webengage?.track(isSolution ? 'Watched Video Solution' : 'Watched Video Analysis', {
      'User Id': user?.email,
      'Question Id': questionId,
      'Test Id': testId,
      'Test Title': testTitle,
      'Video Id': videoId,
    });
  };

  useEffect(() => {
    if (!autoLoad || !videoId) return;
    let cancelled = false;
    setAutoLoadPending(true);
    setAutoLoadError(false);
    setShowPlayer(false);
    (async () => {
      const videoOtp = await getVideoCipherOtp();
      if (cancelled) return;
      if (!videoOtp?.otp || !videoOtp?.playBackInfo) {
        setAutoLoadError(true);
        setAutoLoadPending(false);
        return;
      }
      setOtp(videoOtp.otp);
      setPlaybackInfo(videoOtp.playBackInfo);
      setShowPlayer(true);
      setAutoLoadPending(false);
      trackPlayback();
    })();
    return () => {
      cancelled = true;
    };
  }, [autoLoad, videoId]);

  const handleShowVdoCipherVideoSolution = async () => {
    const videoOtp = await getVideoCipherOtp();
    if (!videoOtp) return;

    const { otp, playBackInfo } = videoOtp;
    setOtp(otp);
    setPlaybackInfo(playBackInfo);
    setShowPlayer(true);

    trackPlayback();
  };

  let thumbnail = '';
  try {
    if (playbackInfo) {
      const parsed = JSON.parse(playbackInfo);
      thumbnail = parsed?.poster || '';
    }
  } catch {}

  if (autoLoad) {
    return (
      <div className={`relative ${isSolution ? 'w-full max-w-[300px]' : 'w-full'}`}>
        {autoLoadPending && (
          <div className="flex min-h-[200px] w-full items-center justify-center rounded-xl bg-[var(--q3-surface-dim)] text-sm text-[var(--q3-neutral-light)]">
            Loading player…
          </div>
        )}
        {autoLoadError && (
          <div className="flex min-h-[120px] w-full flex-col items-center justify-center gap-2 rounded-xl border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] p-4 text-center text-sm text-[var(--q3-neutral-light)]">
            Could not load the video. Please try again later.
          </div>
        )}
        {showPlayer && !autoLoadPending && (
          <>
            {isSolution ? (
              <iframe
                title="Video"
                src={buildVdoCipherIframeSrc(otp, playbackInfo)}
                allow="encrypted-media; fullscreen"
                allowFullScreen
                loading="lazy"
                style={{
                  border: 0,
                  borderRadius: '16px',
                  display: 'block',
                  width: typeof window !== 'undefined' && window.innerWidth < 768 ? '300px' : '400px',
                  height: typeof window !== 'undefined' && window.innerWidth < 768 ? '200px' : '300px',
                }}
              />
            ) : (
              <div className="w-full aspect-video">
                <iframe
                  title="Video"
                  src={buildVdoCipherIframeSrc(otp, playbackInfo)}
                  allow="encrypted-media; fullscreen"
                  allowFullScreen
                  loading="lazy"
                  className="h-full w-full rounded-2xl border-0"
                  style={{ display: 'block' }}
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      {isSolution && (
        <div className="font-medium text-[13px] text-[var(--q3-neutral-light)] cursor-pointer">VIDEO SOLUTION</div>
      )}

      {/* ✅ STYLE MATCHING SECOND PLAYER ONLY WHEN isSolution === true */}
      <div className={`my-3 relative ${isSolution ? 'w-full max-w-[300px]' : 'w-full'}`}>
        {!showPlayer ? (
          <div className="relative cursor-pointer" onClick={handleShowVdoCipherVideoSolution}>
            <img
              src={thumbnail || videoThumbnail || VideoThumbnail}
              alt="Video Thumbnail"
              className={isSolution ? 'max-w-[300px] rounded-lg' : 'w-full rounded-xl'}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-[var(--q3-surface-dim)] text-[var(--q3-neutral-default)] p-2 border border-[var(--q3-neutral-default)] rounded-full shadow text-sm font-medium flex items-center gap-2">
                <PlayCircleIcon size={20} />
                <span>View {isSolution ? 'Solution' : 'Analysis'}</span>
              </div>
            </div>
          </div>
        ) : (
          <>
            {isSolution ? (
              <iframe
                title="Video"
                src={buildVdoCipherIframeSrc(otp, playbackInfo)}
                allow="encrypted-media; fullscreen"
                allowFullScreen
                loading="lazy"
                style={{
                  border: 0,
                  borderRadius: '16px',
                  display: 'block',
                  width: window.innerWidth < 768 ? '300px' : '400px',
                  height: window.innerWidth < 768 ? '200px' : '300px',
                }}
              />
            ) : (
              <div className="w-full aspect-video">
                <iframe
                  title="Video"
                  src={buildVdoCipherIframeSrc(otp, playbackInfo)}
                  allow="encrypted-media; fullscreen"
                  allowFullScreen
                  loading="lazy"
                  style={{
                    border: 0,
                    borderRadius: '16px',
                    width: '100%',
                    height: '100%',
                    display: 'block',
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VideoCipherPlayer;
